









































import Vue from 'vue';
import { namespace } from 'vuex-class';
import { Component } from 'vue-property-decorator';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { getOverviewUrlBySport } from '@/helpers/sports/routes';

import { SportCategory, SportLocale } from '@/enums/sports';

import { COMPETITION_VALUES, TOP_COMPETITIONS } from '@/components/sports/all/all-hardcoded';

import { SportType } from '@/@types/graphql-types';
import { LocaleResponse } from '@/helpers/locale-helper';
import {
	GetSportsFooterEventsV2Document,
	GetSportsFooterEventsV2Query,
	GetSportsFooterEventsV2QueryVariables,
} from '@/pages/graphql/queries/GetSportsFooterV2.query';

const Language = namespace('language');

const TOP_SPORTS = [
	SportType.Football,
	SportCategory.Motorsport,
	SportType.Tennis,
	SportType.AmericanFootball,
	SportType.Baseball,
];

@Component({
	name: 'SportsDefaultFooter',
	apollo: {
		liveData: {
			query: GetSportsFooterEventsV2Document,
			variables(): GetSportsFooterEventsV2QueryVariables {
				const date = new Date();
				date.setHours(date.getHours() - 2);
				return { ...this.queryVariables, filter: { startAt: { from: date.toISOString() } } };
			},
			update(data) {
				return data;
			},
		},
		upcomingData: {
			query: GetSportsFooterEventsV2Document,
			variables(): GetSportsFooterEventsV2QueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data;
			},
		},
	},
})
export default class SportsDefaultFooter extends Vue {
	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;
	@Language.State webLocale: SportLocale;

	liveData: GetSportsFooterEventsV2Query;
	upcomingData: GetSportsFooterEventsV2Query;
	currentYear = new Date().getFullYear();

	get version() {
		return process.env.VERSION;
	}

	get queryVariables(): GetSportsFooterEventsV2QueryVariables {
		return {
			language: this.language,
			country: this.country,
			first: 5,
			filter: {
				startAt: {
					from: shiftDateWithTimezone(this.localeObject.timezone, 1).toISOString(),
				},
			},
			timezoneOffset: 0,
		};
	}

	get footerSections() {
		return [
			{
				title: this.$t('SPORTS_WEBAPP_TOP_X_LIVE_EVENTS', { x: this.liveEvents.length }),
				links: this.liveEvents,
			},
			{
				title: this.$t('SPORTS_WEBAPP_TOP_X_UPCOMING_EVENTS', { x: this.upcomingEvents.length }),
				links: this.upcomingEvents,
			},
			{
				title: this.$t('SPORTS_WEBAPP_TOP_X_COMPETITIONS', { x: this.topCompetitions.length }),
				links: this.topCompetitions,
			},
			{
				title: this.$t('SPORTS_WEBAPP_TOP_X_SPORTS', { x: this.topSports.length }),
				links: this.topSports,
			},
		];
	}

	get liveEvents() {
		return (
			this.liveData?.sportEventsV2?.edges?.map(event => ({
				text: `${event?.node?.competition?.content?.name}: ${event?.node?.content?.name}`,
				path: event?.node?.content?.fullPath,
			})) ?? []
		);
	}

	get upcomingEvents() {
		return (
			this.upcomingData?.sportEventsV2?.edges?.map(event => ({
				text: `${event?.node?.competition?.content?.name}: ${event?.node?.content?.name}`,
				path: event?.node?.content?.fullPath,
			})) ?? []
		);
	}

	get topSports() {
		return TOP_SPORTS.map(sport => ({
			text: this.$t(`SPORTS_WEBAPP_SPORT_TYPE_${sport}`),
			path: getOverviewUrlBySport(sport, this.webLocale),
		}));
	}

	get topCompetitions() {
		return (
			((TOP_COMPETITIONS[this.webLocale] as string[]) || TOP_COMPETITIONS['default'])
				.map(topId => COMPETITION_VALUES[this.webLocale].find(({ id }) => topId === id))
				// thanks a bunch Typescript :/
				.filter((i): i is typeof COMPETITION_VALUES['de'][0] => typeof i !== 'undefined')
				.map(({ name, fullPath }) => ({ text: name, path: fullPath }))
		);
	}

	scrollToTop() {
		window.scrollTo(0, 0);
	}
}
